/**
 * Debounced save for small metadata fields (title, description). Saves the
 * latest value after a quiet period, and immediately on flush, so leaving
 * the page right after typing never drops the change.
 */
export class DebouncedSaver<T> {
  private last: T;
  private pending: { value: T } | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    initial: T,
    private readonly save: (value: T) => Promise<unknown>,
    private readonly delayMs = 800,
    private readonly handlers: { onError?(error: unknown): void; onSaved?(): void } = {},
  ) {
    this.last = initial;
  }

  /** True while an edit is waiting to be saved. */
  get hasPending(): boolean {
    return this.pending !== null;
  }

  /** A value saved elsewhere (AI, an agent, another tab) becomes the baseline; nothing is sent. */
  rebase(value: T): void {
    this.last = value;
  }

  /** Records the person's edit. Only edits are ever saved, never values loaded from elsewhere. */
  change(value: T): void {
    this.pending = { value };
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.delayMs);
  }

  async flush(): Promise<void> {
    clearTimeout(this.timer);
    const pending = this.pending;
    this.pending = null;
    if (!pending || Object.is(pending.value, this.last)) return;
    const previous = this.last;
    this.last = pending.value;
    try {
      await this.save(pending.value);
      this.handlers.onSaved?.();
    } catch (error) {
      this.last = previous; // not saved: the same value may be retried
      this.handlers.onError?.(error);
    }
  }
}
